/**
 * Global middleware to clear errors when navigating between routes
 * This ensures that error pages don't persist when users navigate to new pages
 *
 * IMPORTANT: The error store is cleared via a one-shot afterEach hook rather than
 * directly in this beforeEach guard. This prevents a race condition where Vue
 * re-renders (showing the old page's slot) between the error being cleared and
 * the route actually changing — which can cause unauthorised page components to
 * briefly mount and fire API calls.
 */
export default defineNuxtRouteMiddleware((to, from) => {
  // Only run on client-side navigation (not initial page load)
  if (import.meta.client && from.path !== to.path) {
    // Defer error-store clearing until AFTER the navigation completes,
    // so the route has fully changed before PageErrorBoundary re-renders its slot.
    const errorStore = useErrorStore()
    if (errorStore.hasError) {
      const router = useRouter()
      const unregister = router.afterEach((_to, _from, failure) => {
        // Only clear if the navigation actually succeeded
        if (!failure && errorStore.hasError) {
          errorStore.clearError()
        }
        unregister() // one-shot – remove the hook after it fires
      })
    }
  }
})
